import { useEffect, useState, type ReactNode } from 'react';
import type { Contact, HouseInfo, InfoSection } from '@guest/shared';
import { adminApi } from '../lib/api.js';
import { Icon } from '../lib/icons.js';
import { Panel } from '../components/ui.js';

const uid = (): string => crypto.randomUUID();

const ICONS = ['info', 'shield', 'sparkle', 'radiator', 'door', 'tree', 'key', 'home', 'check', 'power', 'wifi', 'camera'];

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

export function HouseInfoEditor({ onBack }: { onBack: () => void }): ReactNode {
  const [info, setInfo] = useState<HouseInfo | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    void adminApi.houseInfo().then(setInfo);
  }, []);

  if (!info) return <div className="empty">Loading…</div>;

  const patch = (next: Partial<HouseInfo>): void => {
    setInfo({ ...info, ...next });
    setDirty(true);
    setSaved(false);
  };

  const setSection = (id: string, next: Partial<InfoSection>): void =>
    patch({ sections: info.sections.map((s) => (s.id === id ? { ...s, ...next } : s)) });

  const setContact = (id: string, next: Partial<Contact>): void =>
    patch({ contacts: info.contacts.map((c) => (c.id === id ? { ...c, ...next } : c)) });

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      const result = await adminApi.saveHouseInfo(info);
      setInfo(result);
      setDirty(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2400);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="admin-head">
        <button type="button" className="icon-btn" onClick={onBack} aria-label="Back">
          <Icon name="back" size={17} />
        </button>
        <h1>House notes</h1>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!dirty || saving}
          onClick={() => void save()}
        >
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save'}
        </button>
      </div>

      <p className="editor-hint">
        Only guests granted <strong>House-sitting notes</strong> see any of this. Sections with an
        empty body are hidden from them, so you can leave the ones you do not need blank.
      </p>

      <Panel title="Who to call" icon="info" className="stack">
        {info.contacts.map((c, i) => (
          <div className="contact-edit" key={c.id}>
            <div className="two-col">
              <div>
                <label className="field-label">Name</label>
                <input
                  className="field"
                  value={c.name}
                  placeholder="Nat"
                  onChange={(e) => setContact(c.id, { name: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">Role</label>
                <input
                  className="field"
                  value={c.role}
                  placeholder="Owner"
                  onChange={(e) => setContact(c.id, { role: e.target.value })}
                />
              </div>
            </div>
            <div className="two-col">
              <div>
                <label className="field-label">Phone</label>
                <input
                  className="field"
                  value={c.phone}
                  placeholder="07700 900123"
                  inputMode="tel"
                  onChange={(e) => setContact(c.id, { phone: e.target.value })}
                />
              </div>
              <div>
                <label className="field-label">Note</label>
                <input
                  className="field"
                  value={c.note}
                  placeholder="Has a spare key"
                  onChange={(e) => setContact(c.id, { note: e.target.value })}
                />
              </div>
            </div>
            <div className="row" style={{ gap: 6, marginTop: 8 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => patch({ contacts: move(info.contacts, i, i - 1) })}
              >
                Move up
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => patch({ contacts: info.contacts.filter((x) => x.id !== c.id) })}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() =>
            patch({ contacts: [...info.contacts, { id: uid(), name: '', role: '', phone: '', note: '' }] })
          }
        >
          <Icon name="plus" size={15} />
          Add a contact
        </button>
      </Panel>

      <Panel title="Sections" icon="rooms" className="stack">
        {info.sections.map((s, i) => {
          const open = openId === s.id;
          return (
            <div className="section-edit" key={s.id} data-open={open}>
              <div className="section-head">
                <span className="ctrl-icon" data-on={s.body.trim() !== ''}>
                  <Icon name={s.icon} size={16} />
                </span>
                <button
                  type="button"
                  className="section-title"
                  onClick={() => setOpenId(open ? null : s.id)}
                >
                  {s.title || 'Untitled'}
                  {s.body.trim() === '' && <span className="badge">empty</span>}
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Move up"
                  onClick={() => patch({ sections: move(info.sections, i, i - 1) })}
                >
                  <Icon name="chevron" size={14} style={{ transform: 'rotate(-90deg)' }} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label="Move down"
                  onClick={() => patch({ sections: move(info.sections, i, i + 1) })}
                >
                  <Icon name="chevron" size={14} style={{ transform: 'rotate(90deg)' }} />
                </button>
              </div>

              {open && (
                <div className="section-body">
                  <div className="two-col">
                    <div>
                      <label className="field-label">Title</label>
                      <input
                        className="field"
                        value={s.title}
                        onChange={(e) => setSection(s.id, { title: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="field-label">Icon</label>
                      <select
                        className="field"
                        value={s.icon}
                        onChange={(e) => setSection(s.id, { icon: e.target.value })}
                      >
                        {ICONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <label className="field-label" style={{ marginTop: 10 }}>
                    Body
                  </label>
                  <textarea
                    className="field"
                    rows={8}
                    value={s.body}
                    onChange={(e) => setSection(s.id, { body: e.target.value })}
                  />
                  <p className="editor-hint" style={{ margin: '6px 0 0' }}>
                    Blank line starts a new paragraph. A line beginning “- ” becomes a bullet.
                  </p>
                  <div className="row" style={{ marginTop: 10 }}>
                    <span className="spacer" />
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => patch({ sections: info.sections.filter((x) => x.id !== s.id) })}
                    >
                      Delete section
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => {
            const id = uid();
            patch({ sections: [...info.sections, { id, title: 'New section', icon: 'info', body: '' }] });
            setOpenId(id);
          }}
        >
          <Icon name="plus" size={15} />
          Add a section
        </button>
      </Panel>
    </>
  );
}
